import { dateFormat, getFirstValidPrice, greaterDate, getSocietyStatusInfos } from '@bloomays-lib/utils.shared';
import type {
  IMissionCardPricing,
  ISociety,
  InvoiceStatus,
  ContactMissionRole,
  ContactSocietyRole,
  ContactSide,
} from '@bloomays-lib/types.shared';

const MS_PER_DAY = 86_400_000;

/**
 * Écart en jours calendaires entre deux dates (composantes locales, heure ignorée),
 * équivalent à `date-fns/differenceInCalendarDays`. Helper local car l'import direct de
 * `date-fns` est interdit par l'ESLint de l'app.
 */
export const diffInCalendarDays = (later: Date, earlier: Date): number => {
  const a = new Date(later.getFullYear(), later.getMonth(), later.getDate());
  const b = new Date(earlier.getFullYear(), earlier.getMonth(), earlier.getDate());
  return Math.round((a.getTime() - b.getTime()) / MS_PER_DAY);
};

/**
 * Dérivations pures du Tableau de bord de mission V2 (TEC-3051).
 *
 * Regroupe les règles de gestion transverses (§13 de la spec) sous forme de fonctions
 * pures, testables et réutilisables : statut documentaire, bandeau d'alertes, marge,
 * résolution de groupe de contacts (mission prioritaire / société fallback), contraintes
 * société selon forme juridique + portage. Aucune dépendance React / Apollo ici.
 */

export type DocumentKind = 'contract' | 'id' | 'kbis' | 'urssaf' | 'rcp';
export type DocumentStatus = 'valid' | 'warn' | 'expired' | 'missing' | 'pending';

/** Seuil (en jours) sous lequel un document encore valide passe en alerte douce. */
export const DOCUMENT_EXPIRY_WARN_DAYS = 30;

/**
 * Statut documentaire dérivé (§13) :
 * non uploadé → missing ; uploadé sans date → warn ; expiré (J<0) → expired ;
 * < 30 j → warn ; sinon valid.
 */
export const deriveDocumentStatus = (params: {
  uploaded: boolean;
  expirationDate?: string | Date | null;
  /** `false` pour les documents sans échéance (ex. carte d'identité) : un upload suffit à les valider. */
  requiresExpiration?: boolean;
  referenceDate?: Date;
}): DocumentStatus => {
  const { uploaded, expirationDate, requiresExpiration = true, referenceDate = new Date() } = params;
  if (!uploaded) return 'missing';
  if (!requiresExpiration) return 'valid';
  if (!expirationDate) return 'warn';
  const days = diffInCalendarDays(new Date(expirationDate), referenceDate);
  if (days < 0) return 'expired';
  if (days < DOCUMENT_EXPIRY_WARN_DAYS) return 'warn';
  return 'valid';
};

export type MissionDocument = {
  /** Identifiant stable (clé React, ciblage drawer). */
  id?: string;
  kind: DocumentKind;
  name: string;
  uploaded: boolean;
  uploadDate?: string;
  expirationDate?: string;
  optional?: boolean;
  status: DocumentStatus;
};

/**
 * Libellés (i18n) des documents de la société Bloomer, injectés par la présentation. Le helper est
 * pur (aucune dépendance i18n) : la vue résout ces libellés depuis `fr.json` (`missionDashboard`)
 * puis les passe. `urssaf` suit la forme juridique via `getSocietyTaxesLabel` / clés `TaxesLabel`
 * (« Attestation de règlement des impôts et taxes locales » pour EI/micro et sociétés UE/hors-UE,
 * « Certificat Vigilance URSSAF » pour les sociétés françaises).
 */
export type SocietyDocumentLabels = { kbis: string; urssaf: string; rcp: string };

/**
 * Construit la liste des documents de conformité de la société Bloomer (KBIS, URSSAF, RCP),
 * avec leur statut dérivé. La RCP est toujours optionnelle (§13). Le caractère requis du KBIS et
 * de l'URSSAF suit les règles métier mono-sourcées (`deriveSocietyConstraints` : forme juridique +
 * portage) — un document non requis est marqué `optional`, donc ne lève AUCUNE alerte de
 * régularisation même s'il n'est pas uploadé (ex. l'URSSAF pour une EI / micro-entrepreneur, ou
 * KBIS + URSSAF sous portage). Les libellés sont injectés (`labels`, cf. `SocietyDocumentLabels`).
 */
export const buildBloomerSocietyDocuments = (
  society:
    | Pick<
        ISociety,
        | 'status'
        | 'portage'
        | 'KBIS'
        | 'KbisExpirationDate'
        | 'URSSAFVigilanceCertificate'
        | 'URSSAFVCExpirationDate'
        | 'RCPVigilanceCertificate'
        | 'RCPExpirationDate'
      >
    | null
    | undefined,
  referenceDate: Date = new Date(),
  labels: SocietyDocumentLabels,
): MissionDocument[] => {
  const { kbisRequired, urssafRequired } = deriveSocietyConstraints(
    society?.status ?? undefined,
    society?.portage ?? false,
  );
  const docs: Omit<MissionDocument, 'status'>[] = [
    {
      id: 'kbis',
      kind: 'kbis',
      name: labels.kbis,
      uploaded: Boolean(society?.KBIS),
      expirationDate: society?.KbisExpirationDate ?? undefined,
      optional: !kbisRequired,
    },
    {
      id: 'urssaf',
      kind: 'urssaf',
      name: labels.urssaf,
      uploaded: Boolean(society?.URSSAFVigilanceCertificate),
      expirationDate: society?.URSSAFVCExpirationDate ?? undefined,
      optional: !urssafRequired,
    },
    {
      id: 'rcp',
      kind: 'rcp',
      name: labels.rcp,
      uploaded: Boolean(society?.RCPVigilanceCertificate),
      expirationDate: society?.RCPExpirationDate ?? undefined,
      optional: true,
    },
  ];
  return docs.map((doc) => ({
    ...doc,
    status: deriveDocumentStatus({ uploaded: doc.uploaded, expirationDate: doc.expirationDate, referenceDate }),
  }));
};

export type ContractDocInput = { present: boolean; signed: boolean; signedDate?: string };

/**
 * Un contrat légal dont le `step` est un « Avenant N » est un avenant, PAS le contrat principal.
 * (Côté serveur, les avenants sont créés avec `step = "Avenant N"` ; le principal a « Contrat principal » / « CGV ».)
 */
export const isAmendmentStep = (step?: string | null): boolean => /avenant/i.test(step ?? '');

/**
 * Sélectionne le contrat PRINCIPAL (non-avenant) d'un type donné (PDS/SST) dans la liste plate
 * renvoyée par `getLegalContractsByMission` (qui mélange principal + avenants). Mono-sourcé entre les
 * alertes du dashboard et le drawer Documents pour que l'affichage ET le `recordId` ciblé par l'upload
 * portent sur le step principal, jamais sur un avenant.
 */
export const getPrincipalContract = <T extends { type?: string | null; step?: string | null }>(
  contracts: readonly T[] | undefined,
  type: 'PDS' | 'SST',
): T | undefined => contracts?.find((c) => c.type === type && !isAmendmentStep(c.step));

/**
 * Liste ordonnée des documents de la mission (§7) : contrat PDS (client), contrat SST
 * (fournisseur), pièce d'identité (CI du Bloomer), puis KBIS / URSSAF / RCP de la société
 * Bloomer. La CI n'a pas d'échéance (valide si présente, sinon manquante).
 */
export const buildMissionDocuments = (params: {
  pds?: ContractDocInput;
  sst?: ContractDocInput;
  credentialID?: string | null;
  society?: Parameters<typeof buildBloomerSocietyDocuments>[0];
  referenceDate?: Date;
  /** Libellés i18n des documents société (KBIS/URSSAF/RCP), résolus par la vue (cf. `SocietyDocumentLabels`). */
  societyDocLabels: SocietyDocumentLabels;
}): MissionDocument[] => {
  const referenceDate = params.referenceDate ?? new Date();
  const contracts: MissionDocument[] = [];
  // Affichage data-driven (décisions #2/#3 + retour design) : on ne rend une ligne de contrat que si
  // le contrat existe ET n'est PAS signé (état « en cours »). Les contrats signés (valid) sont masqués,
  // les absents ne produisent pas de ligne (ils alimentent l'alerte via `computeContractAlerts`).
  const addContract = (id: string, name: string, contract?: ContractDocInput) => {
    if (!contract?.present || contract.signed) return;
    contracts.push({
      id,
      kind: 'contract',
      name,
      uploaded: true,
      uploadDate: contract.signedDate,
      status: 'pending',
    });
  };
  addContract('contract-pds', 'Contrat PDS (client)', params.pds);
  addContract('contract-sst', 'Contrat SST (fournisseur)', params.sst);
  return [
    ...contracts,
    {
      id: 'ci',
      kind: 'id',
      name: "Pièce d'identité (CI)",
      uploaded: Boolean(params.credentialID),
      status: params.credentialID ? 'valid' : 'missing',
    },
    ...buildBloomerSocietyDocuments(params.society, referenceDate, params.societyDocLabels),
  ];
};

export type MissionAlert = {
  kind: DocumentKind;
  name: string;
  /**
   * Nature de l'anomalie : `missing` (rouge) prime sur `expired` (jaune) ; `signing` (contrat
   * PDS/SST en cours de signature) → bleu, escaladé en rouge via `urgent` (mission démarrée).
   */
  status: 'missing' | 'expired' | 'signing';
  expirationDate?: string;
  /** Pour `signing` : mission déjà démarrée → rendu rouge (urgence) au lieu de bleu. */
  urgent?: boolean;
};

/**
 * Bandeau d'alertes (§3) dérivé des documents. Couleurs (retour design) : `missing` → rouge (prioritaire),
 * `expired` → jaune. Les documents optionnels manquants et les contrats « en cours » ne lèvent rien.
 */
export const computeMissionAlerts = (documents: MissionDocument[]): MissionAlert[] => {
  const missing: MissionAlert[] = documents
    .filter((doc) => doc.status === 'missing' && !doc.optional)
    .map((doc) => ({ kind: doc.kind, name: doc.name, status: 'missing' }));
  const expired: MissionAlert[] = documents
    .filter((doc) => doc.status === 'expired')
    .map((doc) => ({ kind: doc.kind, name: doc.name, status: 'expired', expirationDate: doc.expirationDate }));
  return [...missing, ...expired];
};

/**
 * Alertes contrat (§3, item 10) pour PDS (client) et SST (fournisseur) :
 * - contrat absent → `missing` (rouge) ;
 * - contrat présent mais non signé (« en cours de signature ») → `signing` (bleu), escaladé en
 *   rouge (`urgent`) si la mission a déjà démarré — c'est traité comme une anomalie, à l'instar d'un
 *   document expiré ;
 * - contrat signé → aucune alerte.
 * SST et PDS sont attendus par défaut (aligné créa).
 */
export const computeContractAlerts = (params: {
  pds?: ContractDocInput;
  sst?: ContractDocInput;
  missionStarted?: boolean;
}): MissionAlert[] => {
  const alerts: MissionAlert[] = [];
  const evaluate = (name: string, contract?: ContractDocInput) => {
    if (!contract?.present) {
      alerts.push({ kind: 'contract', name, status: 'missing' });
    } else if (!contract.signed) {
      alerts.push({ kind: 'contract', name, status: 'signing', urgent: Boolean(params.missionStarted) });
    }
  };
  evaluate('Contrat SST (fournisseur)', params.sst);
  evaluate('Contrat PDS (client)', params.pds);
  return alerts;
};

/**
 * Libellé d'échéance contextuel d'un document (§7), formaté en français.
 * missing → « Non uploadé » ; sans date → « Uploadé le {d} » (si connu) ;
 * expiré → « Expiré le {d} » ; < 30 j → « Expire le {d} · J-n » ; sinon « Valide jusqu'au {d} ».
 */
export const formatDocumentExpirationLabel = (doc: MissionDocument, referenceDate: Date = new Date()): string => {
  if (doc.status === 'pending') return 'Signature en cours';
  if (doc.status === 'missing') return 'Non uploadé';
  if (!doc.expirationDate) {
    return doc.uploadDate ? `Uploadé le ${dateFormat(doc.uploadDate, 'dd/MM/yyyy')}` : 'Uploadé';
  }
  const formatted = dateFormat(doc.expirationDate, 'dd/MM/yyyy');
  if (doc.status === 'expired') return `Expiré le ${formatted}`;
  if (doc.status === 'warn') {
    const days = diffInCalendarDays(new Date(doc.expirationDate), referenceDate);
    return `Expire le ${formatted} · J-${days}`;
  }
  return `Valide jusqu'au ${formatted}`;
};

/**
 * Taux de marge en % (§5) : round((vente − achat) / achat × 100).
 * Retourne undefined si l'achat est absent ou nul (garde-fou anti-division par zéro).
 */
export const computeMarginRate = (
  purchasePricePerDayWithoutTaxes?: number | null,
  salePricePerDayWithoutTaxes?: number | null,
): number | undefined => {
  if (!purchasePricePerDayWithoutTaxes || purchasePricePerDayWithoutTaxes <= 0) return undefined;
  if (salePricePerDayWithoutTaxes == null) return undefined;
  return Math.round(
    ((salePricePerDayWithoutTaxes - purchasePricePerDayWithoutTaxes) / purchasePricePerDayWithoutTaxes) * 100,
  );
};

export type ContactGroupSource = 'mission' | 'society' | 'none';

/**
 * Résolution d'un groupe de contacts (§13) : s'il existe ≥1 contact au niveau mission,
 * la source est `mission` (la société est court-circuitée) ; sinon fallback `society` ;
 * sinon `none`. Les entrées falsy sont ignorées.
 */
export const resolveContactGroup = <T>(params: {
  missionContacts?: (T | null | undefined)[] | null;
  societyContacts?: (T | null | undefined)[] | null;
}): { source: ContactGroupSource; contacts: T[] } => {
  const mission = (params.missionContacts ?? []).filter((c): c is T => Boolean(c));
  if (mission.length > 0) return { source: 'mission', contacts: mission };
  const society = (params.societyContacts ?? []).filter((c): c is T => Boolean(c));
  if (society.length > 0) return { source: 'society', contacts: society };
  return { source: 'none', contacts: [] };
};

export type ContactSlotKey =
  | 'client.operational'
  | 'client.billing'
  | 'client.legal'
  | 'supplier.billing'
  | 'supplier.legal';

/**
 * Emplacement de contact d'une mission (§8) : un couple (côté, rôle) avec les niveaux de liaison
 * autorisés. Le niveau société n'existe pas pour `operational` (mission-only, côté client). Sert de
 * source unique aux drawers d'ajout/édition/gestion et au calcul des alarmes de non-conformité —
 * la sélection des contrats légaux (PDS client / SST fournisseur) en dépend.
 */
export type ContactSlot = {
  key: ContactSlotKey;
  side: ContactSide;
  missionRole: ContactMissionRole;
  /** Rôle équivalent au niveau société (absent pour `operational`, mission-only). */
  societyRole?: ContactSocietyRole;
  label: string;
  allowMission: boolean;
  allowSociety: boolean;
  /** Min 1 contact attendu → alarme NON bloquante si vide (décision spec #4). */
  required: boolean;
  /** Contact unique (1 max) : l'ajout remplace le contact existant (contacts légaux PDS/SST). */
  single?: boolean;
};

export const CONTACT_SLOTS: ContactSlot[] = [
  {
    key: 'client.operational',
    side: 'client',
    missionRole: 'operational',
    label: 'Opérationnel',
    allowMission: true,
    allowSociety: false,
    required: true,
    single: true,
  },
  {
    key: 'client.billing',
    side: 'client',
    missionRole: 'billing',
    societyRole: 'billing',
    label: 'Facturation',
    allowMission: true,
    allowSociety: true,
    required: false,
  },
  {
    key: 'client.legal',
    side: 'client',
    missionRole: 'legal',
    societyRole: 'legal',
    label: 'Légal (PDS)',
    allowMission: true,
    allowSociety: true,
    required: false,
    single: true,
  },
  {
    key: 'supplier.billing',
    side: 'supplier',
    missionRole: 'billing',
    societyRole: 'billing',
    label: 'Facturation',
    allowMission: false,
    allowSociety: true,
    required: false,
  },
  {
    key: 'supplier.legal',
    side: 'supplier',
    missionRole: 'legal',
    societyRole: 'legal',
    label: 'Légal (SST)',
    allowMission: true,
    allowSociety: true,
    required: true,
    single: true,
  },
];

export const getContactSlot = (key: ContactSlotKey): ContactSlot =>
  CONTACT_SLOTS.find((slot) => slot.key === key) as ContactSlot;

export type ContactAlarm = { key: ContactSlotKey; side: ContactSide; label: string };

/**
 * Alarmes de non-conformité (§8, décision spec #4) : un emplacement requis sans aucun contact
 * résolu (mission + société confondus) lève une alarme NON bloquante. `counts` = nombre de contacts
 * résolus par emplacement ; une clé absente est traitée comme vide.
 */
export const deriveContactAlarms = (counts: Partial<Record<ContactSlotKey, number>>): ContactAlarm[] =>
  CONTACT_SLOTS.filter((slot) => slot.required && (counts[slot.key] ?? 0) === 0).map((slot) => ({
    key: slot.key,
    side: slot.side,
    label: slot.label,
  }));

export type SocietyConstraints = {
  kbisRequired: boolean;
  urssafRequired: boolean;
  tvaVisible: boolean;
  ibanRequired: boolean;
  countryRequired: boolean;
};

/**
 * Contraintes société fournisseur (§13) selon forme juridique + portage.
 * S'appuie sur `getSocietyStatusInfos` pour classer la forme (micro / UE / hors-UE / FR).
 * Le portage assouplit : KBIS, URSSAF et IBAN non requis ; TVA et pays suivent la forme.
 */
export const deriveSocietyConstraints = (status: string | undefined, portage = false): SocietyConstraints => {
  const { isEuropeanSociety, isForeignSociety, isMicroEntrepreneur } = getSocietyStatusInfos(status);

  // TVA masquée pour les sociétés UE/hors-UE (auto-liquidation) ; visible sinon.
  const tvaVisible = !isEuropeanSociety && !isForeignSociety;
  // Pays d'immatriculation requis uniquement pour les sociétés UE/hors-UE.
  const countryRequired = isEuropeanSociety || isForeignSociety;
  // URSSAF non requise pour les micro-entrepreneurs ; requise sinon.
  const urssafRequired = !isMicroEntrepreneur;

  if (portage) {
    return { kbisRequired: false, urssafRequired: false, tvaVisible, ibanRequired: false, countryRequired };
  }
  return { kbisRequired: true, urssafRequired, tvaVisible, ibanRequired: true, countryRequired };
};

type WithMonth = { activityMonth?: string };
const sameYearMonth = (value: string | undefined, ref: Date): boolean => {
  if (!value) return false;
  const d = new Date(value);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
};

/**
 * Sélectionne le CRA à afficher (§6) : le mois en cours s'il existe, sinon le mois
 * précédent (cas fréquent en début de mois où le CRA n'est pas encore créé), sinon le
 * CRA le plus récent disponible.
 */
export const pickCurrentOrPreviousCra = <T extends WithMonth>(
  activities: T[] | undefined,
  referenceDate: Date = new Date(),
): T | undefined => {
  if (!activities || activities.length === 0) return undefined;
  const previousMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - 1, 1);
  return (
    activities.find((a) => sameYearMonth(a.activityMonth, referenceDate)) ??
    activities.find((a) => sameYearMonth(a.activityMonth, previousMonth)) ??
    [...activities].sort(
      (a, b) => new Date(b.activityMonth ?? 0).getTime() - new Date(a.activityMonth ?? 0).getTime(),
    )[0]
  );
};

/** Vrai si le JSON `nbDaysDetails` contient au moins une journée travaillée (duration > 0).
 * Sert à privilégier, pour l'aperçu calendaire, un CRA réellement détaillé plutôt qu'un CRA dont
 * seul l'agrégat (nbDaysWorked) est renseigné — sinon la grille s'affiche entièrement « absente ». */
export const hasWorkedDayDetails = (nbDaysDetails?: string | null): boolean => {
  if (!nbDaysDetails) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(nbDaysDetails);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== 'object') return false;
  return Object.values(parsed as Record<string, unknown>).some((value) => {
    const duration = typeof value === 'number' ? value : (value as { duration?: number } | null)?.duration;
    return typeof duration === 'number' && duration > 0;
  });
};

/** Cumul des jours travaillés (incl. majorés) des CRA d'une année donnée. */
export const sumWorkedDaysForYear = (
  activities:
    | (
        | { activityMonth?: string; nbDaysWorked?: number; nbDaysMarkupWorked?: number; nbDaysMarkup2Worked?: number }
        | undefined
      )[]
    | undefined,
  year: number,
): number => {
  if (!activities) return 0;
  return activities.reduce((sum, a) => {
    if (!a || new Date(a.activityMonth ?? 0).getFullYear() !== year) return sum;
    return sum + (a.nbDaysWorked ?? 0) + (a.nbDaysMarkupWorked ?? 0) + (a.nbDaysMarkup2Worked ?? 0);
  }, 0);
};

export type InvoiceProgress = {
  percent: number;
  bar: 'green' | 'amber' | 'red' | 'neutral';
  chip: 'success' | 'warning' | 'error' | 'neutral';
  label: string;
};

/**
 * Progression / couleur d'une ligne de facturation à partir du statut client (§ KPI) :
 * « Créée et finalisée » → vert 100% ; « Créée à valider » / « En cours » → ambre 100% ;
 * « En erreur » → rouge 50% ; « Annulée » → gris 100% ; « Non créée » (défaut) → gris 0%.
 */
export const deriveClientInvoiceProgress = (status?: InvoiceStatus | null): InvoiceProgress => {
  switch (status) {
    case 'Créée et finalisée':
      return { percent: 100, bar: 'green', chip: 'success', label: 'Finalisée' };
    case 'Créée à valider':
      return { percent: 100, bar: 'amber', chip: 'warning', label: 'À valider' };
    case 'En cours':
      return { percent: 100, bar: 'amber', chip: 'warning', label: 'En cours' };
    case 'En erreur':
      return { percent: 50, bar: 'red', chip: 'error', label: 'En erreur' };
    case 'Annulée':
      return { percent: 100, bar: 'neutral', chip: 'neutral', label: 'Annulée' };
    case 'Non créée':
    default:
      return { percent: 0, bar: 'neutral', chip: 'neutral', label: status ?? 'Non créée' };
  }
};

/**
 * Tarification active à la date de début de mission (ou aujourd'hui si la mission a déjà
 * commencé), en réutilisant les helpers partagés. Renvoie undefined si aucun prix valide.
 */
export const getActiveMissionPricing = (
  prices: IMissionCardPricing[] | undefined,
  beginMission: string | Date | undefined,
): IMissionCardPricing | undefined => {
  if (!prices || prices.length === 0) return undefined;
  const reference = greaterDate(new Date(beginMission ?? new Date()), new Date());
  return getFirstValidPrice([...prices], reference);
};
